import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Grid, Paper, Typography, Card, CardContent, Button, Chip,
  List, Divider, LinearProgress, Tooltip
} from '@mui/material';
import {
  People as PeopleIcon, School as SchoolIcon, MeetingRoom as RoomIcon,
  AttachMoney as MoneyIcon, Warning as WarningIcon, Schedule as ScheduleIcon,
  Refresh as RefreshIcon, ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon, NotificationImportant as AlertIcon,
  HowToReg as PreRegIcon, ThumbUp as AcceptedIcon, ThumbDown as RefusedIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { GlobalStudentSearch } from '../components/GlobalStudentSearch';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { getAuthUrl } from '../../Commun/config/api.config';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');
  const [, setTick] = useState(0); // Trigger re-render

  const userDosId = user?.agency ? parseInt(user.agency, 10) : 1;
  const agencyRefreshTimerSec = academyStore.getAgencyDashboardRefreshTimer(userDosId);
  const [secondsToRefresh, setSecondsToRefresh] = useState(agencyRefreshTimerSec);

  useEffect(() => {
    academyStore.refreshDashboardStats(true);

    const unsub = academyStore.subscribe(() => setTick(t => t + 1));

    if (agencyRefreshTimerSec <= 0) {
      setSecondsToRefresh(0);
      return () => { unsub(); };
    }

    setSecondsToRefresh(agencyRefreshTimerSec);

    const interval = setInterval(() => {
      setSecondsToRefresh(prev => {
        if (prev <= 1) {
          academyStore.initFromDb();
          return agencyRefreshTimerSec;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      unsub();
      clearInterval(interval);
    };
  }, [agencyRefreshTimerSec]);

  const handleManualRefresh = async () => {
    await academyStore.refreshDashboardStats(true);
    setSecondsToRefresh(agencyRefreshTimerSec);
    setTick(t => t + 1);
  };

  const handleInitializeCache = async () => {
    // boubaker 22/08/2026 - IndexDB multi-utilisateurs :
    // supprimer côté serveur les lignes TABP_SYS_USER_IDX_DB de cet utilisateur
    // (agence + application) puis vider tous les object stores IndexedDB locaux.
    try {
      await axios.post(getAuthUrl('IDXDB_RESET'));
    } catch { /* silencieux */ }
    await academyStore.resetCache();
    setTick(t => t + 1);
  };

  const kpiList = academyStore.getKpiList();
  const kpiData = academyStore.getKpiData();

  // boubaker 25/08/2026 - Tri par ordre configuré + largeur responsive (span /12)
  // par breakpoint (sm/md/lg) issue de la configuration KPI.
  const sortedKpiList = [...(kpiList || [])].sort((a, b) => (Number(a?.order) || 99) - (Number(b?.order) || 99));

  const getSpanProps = (item: any) => {
    const span = item?.span || {};
    const dft = item?.type === 'widget' ? { sm: 12, md: 12, lg: 12 } : { sm: 6, md: 3, lg: 3 };
    return {
      xs: 12 as const,
      sm: Number(span.sm) || dft.sm,
      md: Number(span.md) || dft.md,
      lg: Number(span.lg) || dft.lg
    };
  };

  const getKpiCardDetails = (kpiName: string, rawVal: any, titleStr: string, descStr: string) => {
    let valDisplay = String(rawVal ?? 0);
    if (kpiName === 'totalRevenue') {
      valDisplay = `${currencySymbol}${Number(rawVal ?? 0).toLocaleString()}`;
    }

    let bg = 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)';
    let route = '/Academy/students';
    let icon = <PeopleIcon sx={{ opacity: 0.8 }} />;

    switch (kpiName) {
      case 'totalActiveStudents':
        bg = 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)';
        route = '/Academy/students';
        icon = <PeopleIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalTeachers':
        bg = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)';
        route = '/Academy/teachers';
        icon = <SchoolIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalRooms':
        bg = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
        route = '/Academy/rooms';
        icon = <RoomIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalRevenue':
        bg = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
        route = '/Academy/financial';
        icon = <MoneyIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'pendingPreRegistrations':
        bg = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
        route = '/Academy/students';
        icon = <PreRegIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'acceptedPreRegistrations':
        bg = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
        route = '/Academy/students';
        icon = <AcceptedIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'refusedPreRegistrations':
        bg = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
        route = '/Academy/students';
        icon = <RefusedIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalCourses':
        bg = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)';
        route = '/Academy/courses';
        icon = <SchoolIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalGroups':
        bg = 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)';
        route = '/Academy/groups';
        icon = <PeopleIcon sx={{ opacity: 0.8 }} />;
        break;
      case 'totalQuizzes':
        bg = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
        route = '/Academy/quizzes';
        icon = <SchoolIcon sx={{ opacity: 0.8 }} />;
        break;
      default:
        break;
    }

    return { valDisplay, bg, route, icon };
  };

  const renderWidgetKpi = (name: string) => {
    switch (name) {
      case 'todayPreRegistrations': {
        const todayPreRegs: any[] = kpiData.todayPreRegistrations || [];
        return (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PreRegIcon color="warning" />
                <Typography variant="h6" fontWeight="bold">
                  {t('academy.dashboard.prereg.today_title', todayPreRegs.length)}
                </Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/Academy/students')}>
                {t('academy.dashboard.prereg.view_all')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {todayPreRegs.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">{t('academy.dashboard.prereg.today_empty')}</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {todayPreRegs.map((student: any) => (
                  <Paper key={student.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2, borderColor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Chip label={t('academy.students.status.preregistered')} color="warning" size="small" icon={<WarningIcon />} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">{student.first_name} {student.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.category === 'junior' ? t('academy.students.form.junior') : t('academy.students.form.adult')}
                          {student.phone ? ` • ${student.phone}` : ''}
                          {student.course_title ? ` • ${t('academy.dashboard.prereg.course')}: ${student.course_title}` : ''}
                          {student.time_preference ? ` • ${t('academy.dashboard.prereg.time_preference')}: ${student.time_preference}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Button size="small" variant="outlined" color="primary" onClick={() => navigate('/Academy/students')}>
                      {t('academy.dashboard.prereg.view_all')}
                    </Button>
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        );
      }
      case 'todaysSchedule': {
        const todaySess: any[] = kpiData.todaysSchedule || [];
        return (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {t('academy.dashboard.schedule.today_title', todaySess.length)}
                </Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/Academy/scheduler')}>
                {t('academy.dashboard.schedule.full_scheduler')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {todaySess.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">{t('academy.dashboard.schedule.empty')}</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {todaySess.map((session: any) => (
                  <Paper key={session.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2, borderColor: session.attendance_taken ? 'success.light' : 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label={`${session.start_time} - ${session.end_time}`} color="primary" variant="outlined" size="small" sx={{ fontWeight: 'bold' }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">{session.group_name || t('academy.common.group')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('academy.dashboard.schedule.instructor')} {session.teacher_name} • {t('academy.dashboard.schedule.room')} {session.room_number} ({session.floor})
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {session.attendance_taken ? (
                        <Chip label={t('academy.dashboard.schedule.att_recorded')} color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Button size="small" variant="contained" color="primary" onClick={() => navigate('/Academy/attendance')}>
                          {t('academy.dashboard.schedule.take_att')}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        );
      }
      case 'lowAttendanceAlerts': {
        const alerts: any[] = kpiData.lowAttendanceAlerts || [];
        return (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AlertIcon color="error" />
              <Typography variant="h6" fontWeight="bold">{t('academy.dashboard.alerts.title')}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {t('academy.dashboard.alerts.subtitle')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {alerts.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'success.main', bgcolor: 'success.50', borderRadius: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">{t('academy.dashboard.alerts.all_clear')}</Typography>
                <Typography variant="caption">{t('academy.dashboard.alerts.all_clear_sub')}</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {alerts.map((alert: any) => (
                  <Paper key={alert.session_id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2, borderColor: 'error.light', bgcolor: 'error.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="error.main" fontWeight="bold">{alert.group_name}</Typography>
                      <Chip label={t('academy.dashboard.alerts.rate', alert.attendance_rate)} color="error" size="small" sx={{ fontWeight: 'bold' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('academy.dashboard.schedule.room')} {alert.room_number} • {t('academy.dashboard.alerts.present_count', alert.present_count, alert.total_expected)}
                    </Typography>
                    <LinearProgress variant="determinate" value={alert.attendance_rate} color="error" sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        );
      }
      case 'totalOutstandingDebt': {
        const totalOutstanding = Number(kpiData.totalOutstandingDebt ?? 0);
        const widgetItem = sortedKpiList.find((i: any) => i.name === 'totalOutstandingDebt');
        const thresholds: Array<{ min?: number; max?: number; color: string; borderColor: string; statusKey: string }> = widgetItem?.widgetConfig?.thresholds || [
          { min: 0, max: 1000, color: '#10b981', borderColor: '#059669', statusKey: 'academy.dashboard.status.healthy' },
          { min: 1000, max: 5000, color: '#f97316', borderColor: '#ea580c', statusKey: 'academy.dashboard.status.warning' },
          { min: 5000, color: '#ef4444', borderColor: '#dc2626', statusKey: 'academy.dashboard.status.critical' }
        ];
        const match = thresholds.find(tr => totalOutstanding >= (tr.min ?? 0) && (tr.max === undefined || totalOutstanding <= tr.max)) || thresholds[thresholds.length - 1];
        const debtBg = match.color;
        const debtBorder = match.borderColor;
        const debtStatus = t(match.statusKey);
        return (
          <Paper
            onClick={() => navigate(widgetItem?.widgetConfig?.route || '/Academy/financial')}
            elevation={4}
            sx={{
              p: 3, borderRadius: 3, height: '100%', backgroundColor: debtBg, color: 'white',
              borderLeft: `8px solid ${debtBorder}`, cursor: 'pointer', transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.01)' }, display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between', gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <WarningIcon sx={{ fontSize: 36 }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {t(widgetItem?.translationKey || 'academy.dashboard.widget.outstanding_debt')}
                  </Typography>
                  <Chip label={debtStatus} color="default" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 'bold' }} size="small" />
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t(widgetItem?.desc || 'academy.dashboard.widget.debt_desc')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h3" fontWeight="bold">{currencySymbol}{totalOutstanding.toLocaleString()}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t(widgetItem?.widgetConfig?.manageKey || 'academy.dashboard.widget.manage_debtors')}
              </Typography>
            </Box>
          </Paper>
        );
      }
      default:
        return null;
    }
  };

  const renderDynamicKpis = () => {
    if (!kpiList || kpiList.length === 0) return null;

    return (
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {sortedKpiList.map((item: any) => {
          if (item.type === 'widget') {
            return (
              <Grid item {...getSpanProps(item)} key={item.name}>
                {renderWidgetKpi(item.name)}
              </Grid>
            );
          }
          const rawVal = kpiData[item.name] ?? 0;
          const titleText = t(item.translationKey || '', item.title || item.name);
          const descText = item.desc ? t(item.desc, item.desc) : '';
          const { valDisplay, bg, route, icon } = getKpiCardDetails(item.name, rawVal, titleText, descText);

          return (
            <Grid item {...getSpanProps(item)} key={item.name}>
              <Card
                onClick={() => navigate(route)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                  background: bg,
                  color: 'white'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 'bold' }}>
                      {titleText}
                    </Typography>
                    {icon}
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ my: 1 }}>
                    {valDisplay}
                  </Typography>
                  {descText && (
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {descText}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflowY: 'auto', pb: 4 }}>
      {/* Header & Global Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('academy.dashboard.title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
            {t('academy.dashboard.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <GlobalStudentSearch />
          </Box>
          <Tooltip title={
            agencyRefreshTimerSec <= 0
              ? t('academy.dashboard.refresh_tooltip_disabled', 'Actualisation automatique désactivée')
              : agencyRefreshTimerSec < 60
                ? t('academy.dashboard.refresh_tooltip_sec', `Actualisation automatique toutes les ${agencyRefreshTimerSec} sec`, agencyRefreshTimerSec)
                : t('academy.dashboard.refresh_tooltip_min', `Actualisation automatique toutes les ${Math.round(agencyRefreshTimerSec / 60)} min`, Math.round(agencyRefreshTimerSec / 60))
          }>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleManualRefresh}
              sx={{ whiteSpace: 'nowrap', borderRadius: 2, flexShrink: 0 }}
            >
              {agencyRefreshTimerSec <= 0
                ? 'Actualiser'
                : t('academy.dashboard.refresh_btn', Math.floor(secondsToRefresh / 60), secondsToRefresh % 60)}
            </Button>
          </Tooltip>
          <Tooltip title={t('academy.dashboard.init_tooltip')}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DeleteSweepIcon />}
              onClick={handleInitializeCache}
              sx={{ whiteSpace: 'nowrap', borderRadius: 2, flexShrink: 0, borderColor: 'rgba(255,255,255,0.4)', color: 'white' }}
            >
              {t('academy.dashboard.init_btn')}
            </Button>
          </Tooltip>
        </Box>

      </Paper>

      {/* Dynamic KPI Cards Grid (Renders ONLY enabled KPIs configured in kpiList for user profile) */}
      {renderDynamicKpis()}

    </Box>
  );
};

export default DashboardPage;
